import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, Search, FileCheck2, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  SUBMITTED:    { label: "Received",          tone: "neutral", line: "Your file has been received and is queued for AI screening." },
  AI_SCREENING: { label: "AI screening",      tone: "neutral", line: "An AI is reviewing for spam, duplicates, and evidence quality." },
  AI_RESEARCH:  { label: "AI research",       tone: "neutral", line: "An AI is gathering verifiable sources and a draft outline." },
  HUMAN_REVIEW: { label: "Editorial review",  tone: "neutral", line: "A human editor is reviewing this file. Decisions usually take 1-3 days." },
  APPROVED:     { label: "Approved",          tone: "good",    line: "Approved by the editor. Publication is imminent." },
  PUBLISHED:    { label: "Published",         tone: "good",    line: "Live in the archive." },
  REJECTED:     { label: "Rejected",          tone: "bad",     line: "Not accepted into the archive." },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, tone: "neutral", line: "" };
  const cls =
    meta.tone === "good"
      ? "border-[#5DD4B3]/50 bg-[#5DD4B3]/10 text-[#5DD4B3]"
      : meta.tone === "bad"
        ? "border-[#D92D20]/60 bg-[#D92D20]/10 text-[#FFA9A0]"
        : "border-[#454038] bg-[#1a1916] text-[#CBB892]";
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.3em] border px-3 py-1 ${cls}`}>
      {meta.label}
    </span>
  );
};

const StatusCard = ({ data }) => {
  const meta = STATUS_META[data.status] || { line: "" };
  return (
    <div
      data-testid="status-card"
      className="border border-[#2D2A26] bg-[#131313] p-8 relative aged-paper"
    >
      <div className="absolute top-3 right-3 stamp">
        {data.status === "PUBLISHED" ? "PUBLISHED" : data.status === "REJECTED" ? "CLOSED" : "ON FILE"}
      </div>
      <div className="flex items-center gap-3 mb-5">
        {data.status === "REJECTED"
          ? <AlertCircle size={22} className="text-[#D92D20]" />
          : <FileCheck2 size={22} className="text-[#7ad08a]" />}
        <h2 className="font-display text-3xl text-[#E4DFD3]">{data.title}</h2>
      </div>
      <div className="flex items-center gap-3 mb-5">
        <StatusPill status={data.status} />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065]">
          FILED {new Date(data.created_at).toLocaleDateString()}
        </span>
      </div>
      <p className="text-[#A39E93] mb-4">{meta.line}</p>
      {data.status === "REJECTED" && data.rejection_reason && (
        <div className="border-l-2 border-[#D92D20] pl-3 mb-5">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
            // REASON
          </div>
          <p className="text-sm italic text-[#CBB892]">{data.rejection_reason}</p>
        </div>
      )}
      {data.status === "PUBLISHED" && data.published_topic_id && (
        <Link
          to={`/topic/${data.published_topic_id}`}
          data-testid="published-dossier-link"
          className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#E4DFD3] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          Open Dossier
        </Link>
      )}
      <div className="mt-6 pt-5 border-t border-[#2D2A26]">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">// TRACKING ID</div>
        <code className="font-mono text-xs text-[#A39E93] break-all">{data.id}</code>
      </div>
    </div>
  );
};

const Status = () => {
  const nav = useNavigate();
  const { id: idFromUrl } = useParams();
  const [input, setInput] = useState(idFromUrl || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const lookup = async (sid) => {
    const id = (sid || "").trim();
    if (!id) return;
    setLoading(true); setNotFound(false); setData(null);
    try {
      const r = await axios.get(`${BACKEND}/api/submissions/${encodeURIComponent(id)}/status`);
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup if a tracking ID is in the URL
  React.useEffect(() => {
    if (idFromUrl) lookup(idFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFromUrl]);

  const handleSubmit = (e) => {
    e.preventDefault();
    nav(`/status/${encodeURIComponent(input.trim())}`);
    lookup(input.trim());
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="status-page">
      <Link
        to="/submit"
        className="inline-flex items-center gap-2 text-[#A39E93] hover:text-[#D92D20] font-mono text-[10px] uppercase tracking-widest mb-8"
      >
        <ArrowLeft size={12} /> Back to intake
      </Link>

      <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-3">// FILE STATUS</div>
      <h1 className="font-display text-5xl md:text-6xl text-[#E4DFD3] mb-3">Check a file</h1>
      <p className="text-[#A39E93] max-w-2xl mb-10">
        Paste the tracking ID from your submission receipt to see where your file is
        in the editorial pipeline.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-10">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. beafb3ed-9b0b-4710-b00d-da4534049760"
          data-testid="status-input"
          className="flex-1 bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065]"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          data-testid="status-lookup-btn"
          className="flex items-center justify-center gap-2 border border-[#454038] hover:border-[#D92D20] disabled:opacity-40 disabled:cursor-not-allowed text-[#E4DFD3] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          {loading
            ? <><Loader2 size={12} className="animate-spin" /> Looking…</>
            : <><Search size={12} /> Look up</>}
        </button>
      </form>

      {notFound && (
        <div
          data-testid="status-not-found"
          className="border border-[#D92D20]/40 bg-[#1a0808] p-5 mb-6"
        >
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle size={16} className="text-[#D92D20]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              No file found
            </span>
          </div>
          <p className="text-sm text-[#A39E93]">
            That tracking ID doesn't match any submission on file. Double-check the ID, or
            file a new one.
          </p>
        </div>
      )}

      {data && <StatusCard data={data} />}
    </div>
  );
};

export default Status;
